'use client';

import React, { useState, useMemo } from 'react';
import { Box, TextField, Fade, Button, Grid, Paper, CircularProgress } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import useSaveRowCalculations from '../../hooks/useSaveRowCalculations';
import { useEstimator } from '../../context/EstimatorContext';

const RowCalculator = ({ onComplete }) => {
  const { formData, setFormData } = useEstimator();
  const [loading, setLoading] = useState(false);
  const saveRowCalculations = useSaveRowCalculations();

  const [overrideCost, setOverrideCost] = useState(formData?.overrideCost !== undefined ? formData.overrideCost : '');

  const [rowData, setRowData] = useState(() =>
    formData?.rowCalculator?.length
      ? formData.rowCalculator
      : Array.from({ length: 6 }, () => ({
          county: '',
          footage: '',
          costPerFoot: '',
          percentOfRoute: '0',
          totalCost: '-',
        }))
  );

  const isRowNotEmpty = (row) => {
    return row.county?.trim() !== '' || row.footage !== '' || row.costPerFoot !== '' || row.totalCost !== '-';
  };

  const totalCost = rowData.reduce((sum, row) => {
    const cost = parseFloat(row.totalCost);
    return sum + (isNaN(cost) ? 0 : cost);
  }, 0);

  const totalFootage = rowData.reduce((sum, row) => {
    const footage = parseFloat(row.footage);
    return sum + (isNaN(footage) ? 0 : footage);
  }, 0);

  const totalPercentOfRoute = rowData.reduce((sum, row) => {
    const percent = parseFloat(String(row.percentOfRoute).replace('%', ''));

    return sum + (isNaN(percent) ? 0 : percent);
  }, 0);

  const averageCostPerFoot =
    overrideCost && !isNaN(Number(overrideCost))
      ? Number(overrideCost)
      : totalFootage > 0
        ? totalCost / totalFootage
        : 0;

  const averageCostPerRod = averageCostPerFoot * 16.5;

  const handleGridEdit = (event) => {
    const updated = [...rowData];
    const index = event.node.rowIndex;
    const updatedRow = { ...event.data };

    const footage = parseFloat(String(updatedRow.footage).replace(/[^0-9.]/g, ''));
    const costPerFoot = parseFloat(String(updatedRow.costPerFoot).replace(/[^0-9.]/g, ''));
    const totalRouteFootage = Number(formData.quantities?.[2]?.quantity) || 0;

    if (!isNaN(footage) && !isNaN(costPerFoot)) {
      updatedRow.totalCost = (footage * costPerFoot).toFixed(2);
    } else {
      updatedRow.totalCost = '-';
    }

    if (!isNaN(footage) && totalRouteFootage > 0) {
      const percent = (footage / totalRouteFootage) * 100;
      updatedRow.percentOfRoute = `${percent.toFixed(2)}%`;
    } else {
      updatedRow.percentOfRoute = '0';
    }

    updated[index] = updatedRow;
    setRowData(updated);
  };

  const handlePasteEnd = () => {
    const updated = [...rowData];
    const totalRouteFootage = Number(formData.quantities?.[2]?.quantity) || 0;

    updated.forEach((row) => {
      const footage = parseFloat(String(row.footage).replace(/[^0-9.]/g, ''));
      const costPerFoot = parseFloat(String(row.costPerFoot).replace(/[^0-9.]/g, ''));

      row.totalCost = !isNaN(footage) && !isNaN(costPerFoot) ? (footage * costPerFoot).toFixed(2) : '-';

      row.percentOfRoute =
        !isNaN(footage) && totalRouteFootage > 0 ? `${((footage / totalRouteFootage) * 100).toFixed(2)}%` : '0.00%';
    });

    setRowData(updated);
  };

  const handleAddRow = () => {
    setRowData((prev) => [
      ...prev,
      {
        county: '',
        footage: 0,
        costPerFoot: 0,
        percentOfRoute: '0',
        totalCost: '-',
      },
    ]);
  };

  const handleNext = async () => {
    setLoading(true);
    try {
      const filteredRows = rowData.filter(isRowNotEmpty);

      await saveRowCalculations({
        estimateId: formData.estimateId,
        rows: filteredRows,
        overrideCost,
        avgPerFoot: averageCostPerFoot.toFixed(2),
        avgPerRod: averageCostPerRod.toFixed(2),
        totalPercent: totalPercentOfRoute.toFixed(2),
      });

      setFormData((prev) => ({
        ...prev,
        rowCalculator: rowData,
        overrideCost,
        averageROWCostPerFoot: averageCostPerFoot.toFixed(2),
        averageROWCostPerRod: averageCostPerRod.toFixed(2),
        totalPercentOfRoute: totalPercentOfRoute.toFixed(2),
      }));

      onComplete();
    } catch (err) {
      console.error('Failed to save row calculations:', err);
      alert('Failed to save row calculations.');
    } finally {
      setLoading(false);
    }
  };

  const columnDefs = useMemo(
    () => [
      { headerName: 'Counties', field: 'county', editable: true, flex: 1 },
      {
        headerName: 'ft per county',
        field: 'footage',
        editable: true,
        flex: 1,
        valueFormatter: ({ value }) => (value !== '' && !isNaN(Number(value)) ? Number(value).toLocaleString() : value),
      },
      { headerName: '$ / ft', field: 'costPerFoot', editable: true, flex: 1 },
      {
        headerName: 'Percent of Route',
        field: 'percentOfRoute',
        valueFormatter: ({ value }) => value || '',
        editable: false,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
      },
      {
        headerName: 'Total Cost',
        field: 'totalCost',
        flex: 1,
        valueFormatter: ({ value }) =>
          value !== '-' && !isNaN(Number(value))
            ? `$${Number(value).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`
            : value,
        cellStyle: { backgroundColor: '#f0f0f0', color: '#999' },
      },
    ],
    []
  );

  return (
    <Fade in>
      <Box display="flex" flexDirection="column" gap={2} mt={4}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#F9F9F9' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="ROW Cost $/ft Override"
                    value={overrideCost}
                    onChange={(e) => setOverrideCost(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#fff3e0',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Average ROW Cost $/ft"
                    value={`$${averageCostPerFoot.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`}
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#fff',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Average ROW Cost $/rod"
                    value={`$${averageCostPerRod.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`}
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        backgroundColor: '#fff',
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#F9F9F9' }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Total Route Footage"
                    value={
                      formData.quantities?.[2]?.quantity
                        ? Number(formData.quantities[2].quantity).toLocaleString()
                        : '0'
                    }
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Total ROW Cost"
                    value={`$${totalCost.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`}
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                    size="small"
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        <Paper elevation={3} sx={{ p: 3, backgroundColor: '#F9F9F9', borderRadius: 2 }}>
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={{ resizable: true }}
            onCellValueChanged={handleGridEdit}
            onPasteEnd={handlePasteEnd}
            domLayout="autoHeight"
          />
        </Paper>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={handleAddRow}>
            Add Row
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
            startIcon={loading && <CircularProgress size={18} color="inherit" />}
          >
            {loading ? 'Saving...' : 'Next'}
          </Button>
        </Box>
      </Box>
    </Fade>
  );
};

export default RowCalculator;
